import { useEffect, useState } from 'react';
import { AlertTriangle, Check, Circle, Eye, FileText, Landmark, Mail, Paperclip } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { fmtDateTimeEastern } from '../lib/format';
import { DecisionBar } from './ui/DecisionBar';
import { CLAIM_TYPES, derivarClaimType } from '../lib/claimType';

type LorStatus = {
  claim_number: string | null; insurance_company: string | null; insurance_email: string | null;
  claim_type: string | null; type_of_loss: string | null; homeowner_name: string | null;
  lor_sent_at: string | null; filed_at: string | null;
  sends: Array<{
    at: string; by_name: string | null; to: string | null; email_id: string | null;
    email_status: string | null; delivered_at: string | null; opened_at: string | null; clicked_at: string | null;
    manual: boolean; reconocido: boolean; nota: string | null;
  }>;
  contract: { id: string; status: string; signed_at: string | null; sent_at: string | null; url: string | null } | null;
};
type LorFile = { kind: string; label: string; filename: string; url: string | null };

/**
 * LA CAJA DEL LOR, dentro del historial de emails (pedido del dueño 2026-09-09).
 *
 *  - "Send LOR": manda al seguro la plantilla "LOR To Insurance" con el claim number (obligatorio)
 *    y los adjuntos (contrato firmado + W9 + licencia del PA). El correo queda en el hilo de abajo.
 *  - El cuadro de estado: cada envío con ✓, cuándo y quién — como el del contrato. Si se manda
 *    otra vez, sale abajo. Y el contrato del claim, clickeable para abrirlo.
 *  - "Claim filed": pasa el claim a Claim in (y a las colas que le tocan). NO se puede sin LOR.
 */
export function LorBox({ claimId, onSent, onFiled }: { claimId: string; onSent?: () => void; onFiled?: () => void }) {
  const { t, lang } = useI18n();
  const [st, setSt] = useState<LorStatus | null>(null);
  const [files, setFiles] = useState<LorFile[]>([]);
  const [claimNumber, setClaimNumber] = useState('');
  const [to, setTo] = useState('');
  // TIPO DE CLAIM cuando la tarjeta no lo tiene (Claim filed lo exige): un dropdown acá mismo, con
  // la misma derivación del chip (nombre → tipo de pérdida → Roof) preseleccionada.
  const [claimType, setClaimType] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // LA LOR SALIÓ POR OTRO SISTEMA (dueño 2026-10-13): el cuadro pregunta DÓNDE se subió y, al confirmar, marca la LOR y
  // mueve el claim a Claim in en el mismo paso (si falta el claim number o el tipo, avisa y deja la LOR marcada).
  const [ext, setExt] = useState<{ open: boolean; where: 'portal' | 'email_other' | 'fax' | 'mail' | 'phone' | 'other'; detail: string }>({ open: false, where: 'portal', detail: '' });
  const [okMsg, setOkMsg] = useState('');

  const load = () => api.get<LorStatus>(`/api/claims/${claimId}/lor`).then((r) => {
    setSt(r);
    setClaimNumber((prev) => prev || r.claim_number || '');
    setTo((prev) => prev || r.insurance_email || '');
    setClaimType((prev) => prev || derivarClaimType({ claim_type: r.claim_type, homeowner_name: r.homeowner_name, type_of_loss: r.type_of_loss }).tipo || '');
  }).catch(() => {});
  useEffect(() => {
    void load();
    api.get<{ files: LorFile[] }>(`/api/claims/${claimId}/lor-attachments`).then((r) => setFiles(r.files || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  const errores: Record<string, string> = {
    claim_number_required: t('lor.errClaimNumber'), email_required: t('lor.errEmail'), lor_required: t('lor.errLorRequired'),
    insurance_company_required: t('rtf.errInsurance'), claim_type_required: t('rtf.errType'),
  };

  async function enviar() {
    if (!to.trim()) { setErr(t('lor.errEmail')); return; }
    if (!claimNumber.trim()) { setErr(t('lor.errClaimNumber')); return; }
    if (st?.sends.length && !window.confirm(t('lor.resendConfirm'))) return;
    setBusy(true); setErr('');
    try {
      // El tipo elegido se guarda ANTES: mandar la LOR ya filea el claim (caso BMH LLC 2026-09-23)
      // y el filing exige aseguradora + tipo, igual que el botón Claim filed.
      if (faltaTipo && claimType) await api.put(`/api/claims/${claimId}/info`, { claim_type: claimType });
      const r = await api.post<{ filed: boolean; filing_blocked: string | null }>(`/api/claims/${claimId}/send-lor`, { email: to.trim(), claimNumber: claimNumber.trim() });
      await load(); onSent?.();
      if (r.filed) onFiled?.();
      else if (r.filing_blocked) setErr(`${t('lor.sentNotFiled')} ${errores[r.filing_blocked] || r.filing_blocked}`);
    } catch (e) { const raw = e instanceof ApiError ? e.message : 'Error'; setErr(errores[raw] || raw); }
    finally { setBusy(false); }
  }
  const faltaTipo = !!st && !(st.claim_type ?? '').trim() && !(st.type_of_loss ?? '').trim();
  const DONDE: Array<{ key: typeof ext.where; label: string }> = [
    { key: 'portal', label: t('lor.extPortal') }, { key: 'email_other', label: t('lor.extEmailOther') }, { key: 'fax', label: t('lor.extFax') },
    { key: 'mail', label: t('lor.extMail') }, { key: 'phone', label: t('lor.extPhone') }, { key: 'other', label: t('lor.extOther') },
  ];
  async function marcarExterna() {
    const detalle = ext.detail.trim();
    if (!detalle) { setErr(t('lor.extDetailRequired')); return; }
    if (faltaTipo && !claimType) { setErr(t('rtf.errType')); return; }
    setBusy(true); setErr(''); setOkMsg('');
    try {
      if (faltaTipo && claimType) await api.put(`/api/claims/${claimId}/info`, { claim_type: claimType });
      const donde = DONDE.find((d) => d.key === ext.where)?.label ?? ext.where;
      const r = await api.post<{ filed: boolean; filing_blocked: string | null }>(`/api/claims/${claimId}/lor/mark-sent`, {
        note: `${donde}: ${detalle}`, where: ext.where, file: true, claimNumber: claimNumber.trim() || null,
      });
      setExt({ open: false, where: 'portal', detail: '' });
      if (r.filed) { setOkMsg(t('lor.extFiled')); onFiled?.(); }
      else { await load(); setErr(`${t('lor.sentNotFiled')} ${errores[r.filing_blocked ?? ''] || r.filing_blocked || ''}`); }
    }
    catch (e) { const raw = e instanceof ApiError ? e.message : 'Error'; setErr(errores[raw] || raw); }
    finally { setBusy(false); }
  }
  async function filear() {
    if (faltaTipo && !claimType) { setErr(t('rtf.errType')); return; }
    if (!window.confirm(t('rtf.fileConfirm'))) return;
    setBusy(true); setErr('');
    try {
      // El tipo elegido se guarda en la tarjeta ANTES de filear: es la misma columna del chip.
      if (faltaTipo && claimType) await api.put(`/api/claims/${claimId}/info`, { claim_type: claimType });
      await api.post(`/api/claims/${claimId}/claim-filed`, { claimNumber: claimNumber.trim() || null }); onFiled?.();
    }
    catch (e) { const raw = e instanceof ApiError ? e.message : 'Error'; setErr(errores[raw] || raw); }
    finally { setBusy(false); }
  }

  const enviado = (st?.sends.length ?? 0) > 0;
  const k = st?.contract ?? null;
  const contratoTexto = !k ? t('iq.noContract')
    : k.status === 'signed' ? `${t('iq.eSigned')}${k.signed_at ? ` · ${fmtDateTimeEastern(k.signed_at, lang)}` : ''}`
    : `${t('iq.contractSent')}${k.sent_at ? ` · ${fmtDateTimeEastern(k.sent_at, lang)}` : ''}`;
  const contratoColor = !k ? 'var(--red)' : k.status === 'signed' ? 'var(--green)' : 'var(--orange)';

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        <Landmark size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('lor.title')}
      </div>

      {/* Claim # + email del seguro + Send LOR. */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'end' }}>
        <label style={{ display: 'grid', gap: 3, minWidth: 150 }}>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('rtf.claimNumber')}</span>
          <input className="input sm" style={{ fontFamily: 'var(--font-mono)' }} value={claimNumber} onChange={(e) => setClaimNumber(e.target.value)} placeholder="—" />
        </label>
        <label style={{ display: 'grid', gap: 3, flex: 1, minWidth: 200 }}>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('rtf.lorEmail')}</span>
          <input className="input sm" type="email" value={to} placeholder="insurance@carrier.com" onChange={(e) => setTo(e.target.value)} />
        </label>
        <button className="btn sm" disabled={busy} onClick={enviar} title={t('lor.sendHint')}>
          <Mail size={14} strokeWidth={1.75} /> {enviado ? t('lor.sendAgain') : t('rtf.sendLor')}
        </button>
      </div>
      {files.length > 0 ? (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}><Paperclip size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('rtf.lorAttached')}:</span>
          {files.map((f, i) => f.url
            ? <a key={i} href={f.url} target="_blank" rel="noopener noreferrer" className="badge" title={f.filename} style={{ fontSize: 'var(--fs-2xs)', background: 'var(--accent-soft)', color: 'var(--accent)', textDecoration: 'none', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'] }}><FileText size={12} strokeWidth={1.75} /> {f.label} ↗</a>
            : <span key={i} className="badge gray" title={f.filename} style={{ fontSize: 'var(--fs-2xs)' }}><FileText size={12} strokeWidth={1.75} /> {f.label}</span>)}
        </div>
      ) : (
        <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--orange)' }}><AlertTriangle size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('rtf.lorNoFiles')}</div>
      )}

      {/* EL CUADRO DE ESTADO: envíos (✓ cuándo · quién · a quién) + contrato clickeable. */}
      <div style={{ border: '1px solid var(--line)', borderRadius: 8, padding: '8px 10px', background: 'var(--card)', display: 'grid', gap: 4, fontSize: 'var(--fs-xs)' }}>
        {st && st.sends.length === 0 && (
          <div className="muted" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span><Circle size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('lor.notSent')}</span>
            {/* BYPASS (Elizabeth, 2026-09-11; dueño 2026-10-13): la LOR salió por otro sistema. Pregunta dónde y filea. */}
            <button className="ntf-btn ghost" style={{ padding: '2px 8px' }} disabled={busy} onClick={() => setExt((x) => ({ ...x, open: !x.open }))}>{t('lor.markSent')}</button>
          </div>
        )}
        {ext.open && st && st.sends.length === 0 && (
          <div style={{ display: 'grid', gap: 6, padding: '8px 10px', border: '1px dashed var(--line)', borderRadius: 8, background: 'var(--surface-2)' }}>
            <div style={{ fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'] }}>{t('lor.extTitle')}</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'end' }}>
              <label style={{ display: 'grid', gap: 3 }}>
                <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('lor.extWhere')}</span>
                <select className="input sm" value={ext.where} onChange={(e) => setExt((x) => ({ ...x, where: e.target.value as typeof ext.where }))}>
                  {DONDE.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 3, flex: 1, minWidth: 220 }}>
                <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('lor.extDetail')}</span>
                <input className="input sm" value={ext.detail} placeholder={t('lor.extDetailPh')} onChange={(e) => setExt((x) => ({ ...x, detail: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') marcarExterna(); }} />
              </label>
              <button className="btn sm solid-good" disabled={busy} onClick={marcarExterna}><Check size={13} strokeWidth={2} /> {t('lor.extConfirm')}</button>
              <button className="btn ghost sm" disabled={busy} onClick={() => setExt((x) => ({ ...x, open: false }))}>{t('common.cancel')}</button>
            </div>
            <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('lor.extHint')}</div>
          </div>
        )}
        {okMsg && <div style={{ color: 'var(--green)', fontSize: 'var(--fs-xs)' }}>{okMsg}</div>}
        {st?.sends.map((s, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--green)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'] }}><Check size={13} strokeWidth={2.2} style={{ verticalAlign: 'text-bottom' }} /> {i === 0 ? t('rtf.lorSent') : t('lor.sentAgainLabel')}</span>
            <span>{fmtDateTimeEastern(s.at, lang)}</span>
            {s.by_name && <span className="muted">· {t('lor.by')} <b>{s.by_name}</b></span>}
            {s.to && <span className="muted" title={s.to}>· → {s.to}</span>}
            {s.reconocido && <span className="muted" title={t('lor.recognizedHint')}>· {t('lor.recognized')}</span>}
            {s.manual && <span className="muted" title={s.nota ?? ''}>· {t('lor.markedManually')}{s.nota ? `: ${s.nota}` : ''}</span>}
            {/* EL TRACKER DEL CORREO (pedido 2026-09-09): cuándo lo abrieron del otro lado. */}
            {s.email_id && (() => {
              const rebotado = s.email_status === 'bounced' || s.email_status === 'failed' || s.email_status === 'suppressed';
              const abierto = s.opened_at || s.clicked_at;
              const color = rebotado ? 'var(--red)' : abierto ? 'var(--green)' : 'var(--muted)';
              const texto = rebotado ? `${t('lor.bounced')} (${s.email_status})`
                : abierto ? `${t('em.opened')} ${fmtDateTimeEastern(s.opened_at ?? s.clicked_at, lang)}`
                : s.delivered_at ? `${t('em.delivered')} ${fmtDateTimeEastern(s.delivered_at, lang)} · ${t('lor.notOpened')}`
                : t('lor.notOpened');
              return <span style={{ color, fontSize: 'var(--fs-2xs)', flexBasis: '100%', paddingLeft: 18 }}><Eye size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {texto}</span>;
            })()}
          </div>
        ))}
        <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap', borderTop: '1px dashed var(--line)', paddingTop: 4, marginTop: 2 }}>
          <span style={{ color: contratoColor, fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'] }}>
            {k?.status === 'signed' ? <Check size={13} strokeWidth={2.2} style={{ verticalAlign: 'text-bottom' }} /> : <AlertTriangle size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} />} {t('iq.contract')}:
          </span>
          {k?.url
            ? <a href={k.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'] }} title={t('lor.openContract')}>{contratoTexto} ↗</a>
            : <span>{contratoTexto}</span>}
        </div>
      </div>

      {faltaTipo && (
        <label style={{ display: 'grid', gap: 3, maxWidth: 280 }}>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('cdw.claimType')} · {t('lor.claimTypeNeeded')}</span>
          <select className="input sm" value={claimType} onChange={(e) => setClaimType(e.target.value)}>
            <option value="">—</option>
            {CLAIM_TYPES.map((o) => <option key={o} value={o}>{t(`cty.${o.toLowerCase()}`)}</option>)}
          </select>
        </label>
      )}
      {err && <div style={{ color: 'var(--red)', fontSize: 'var(--fs-xs)' }}>{err}</div>}
      {/* Claim filed: mueve el claim → barra de decisión. Sin LOR, apagado y con el porqué. */}
      <DecisionBar hint={enviado ? t('rtf.filedHint') : t('lor.filedNeedsLor')}>
        <button className="btn sm solid-good" disabled={busy || !enviado || !!st?.filed_at} onClick={filear} title={enviado ? '' : t('lor.filedNeedsLor')}>
          <Check size={14} strokeWidth={1.75} /> {t('rtf.claimFiled')}
        </button>
      </DecisionBar>
    </div>
  );
}
